import React, { useMemo, useState } from 'react';
import { Check, Copy, Download } from 'lucide-react';
import { PrismLight as SyntaxHighlighter } from 'react-syntax-highlighter';
import oneDark from 'react-syntax-highlighter/dist/esm/styles/prism/one-dark';

import bash from 'react-syntax-highlighter/dist/esm/languages/prism/bash';
import clike from 'react-syntax-highlighter/dist/esm/languages/prism/clike';
import csharp from 'react-syntax-highlighter/dist/esm/languages/prism/csharp';
import css from 'react-syntax-highlighter/dist/esm/languages/prism/css';
import go from 'react-syntax-highlighter/dist/esm/languages/prism/go';
import java from 'react-syntax-highlighter/dist/esm/languages/prism/java';
import javascript from 'react-syntax-highlighter/dist/esm/languages/prism/javascript';
import json from 'react-syntax-highlighter/dist/esm/languages/prism/json';
import jsx from 'react-syntax-highlighter/dist/esm/languages/prism/jsx';
import kotlin from 'react-syntax-highlighter/dist/esm/languages/prism/kotlin';
import markup from 'react-syntax-highlighter/dist/esm/languages/prism/markup';
import php from 'react-syntax-highlighter/dist/esm/languages/prism/php';
import powershell from 'react-syntax-highlighter/dist/esm/languages/prism/powershell';
import python from 'react-syntax-highlighter/dist/esm/languages/prism/python';
import rust from 'react-syntax-highlighter/dist/esm/languages/prism/rust';
import sql from 'react-syntax-highlighter/dist/esm/languages/prism/sql';
import tsx from 'react-syntax-highlighter/dist/esm/languages/prism/tsx';
import typescript from 'react-syntax-highlighter/dist/esm/languages/prism/typescript';
import yaml from 'react-syntax-highlighter/dist/esm/languages/prism/yaml';

const LANG_ALIASES = {
  js: 'javascript',
  jsx: 'jsx',
  mjs: 'javascript',
  cjs: 'javascript',
  ts: 'typescript',
  tsx: 'tsx',
  cs: 'csharp',
  'c#': 'csharp',
  csharp: 'csharp',
  py: 'python',
  python: 'python',
  sh: 'bash',
  shell: 'bash',
  zsh: 'bash',
  bash: 'bash',
  ps1: 'powershell',
  powershell: 'powershell',
  html: 'markup',
  xml: 'markup',
  svg: 'markup',
  yml: 'yaml',
  yaml: 'yaml',
  json: 'json',
  css: 'css',
  scss: 'css',
  sql: 'sql',
  java: 'java',
  kt: 'kotlin',
  kotlin: 'kotlin',
  go: 'go',
  golang: 'go',
  rs: 'rust',
  rust: 'rust',
  php: 'php',
  c: 'clike',
  cpp: 'clike',
  'c++': 'clike',
  text: 'text',
  plain: 'text',
};

const REGISTERED = new Set(['text']);

[
  ['clike', clike],
  ['markup', markup],
  ['javascript', javascript],
  ['typescript', typescript],
  ['jsx', jsx],
  ['tsx', tsx],
  ['csharp', csharp],
  ['java', java],
  ['kotlin', kotlin],
  ['python', python],
  ['bash', bash],
  ['powershell', powershell],
  ['php', php],
  ['sql', sql],
  ['json', json],
  ['css', css],
  ['yaml', yaml],
  ['go', go],
  ['rust', rust],
].forEach(([name, lang]) => {
  try {
    SyntaxHighlighter.registerLanguage(name, lang);
    REGISTERED.add(name);
  } catch (_) {
    REGISTERED.add(name);
  }
});

const CODE_STYLE = {
  ...oneDark,
  'pre[class*="language-"]': {
    ...(oneDark['pre[class*="language-"]'] || {}),
    background: 'transparent',
    margin: 0,
    padding: 0,
    color: '#abb2bf',
  },
  'code[class*="language-"]': {
    ...(oneDark['code[class*="language-"]'] || {}),
    background: 'transparent',
    color: '#abb2bf',
    textShadow: 'none',
  },
  comment: { color: '#7f848e', fontStyle: 'italic' },
  punctuation: { color: '#abb2bf' },
  property: { color: '#e06c75' },
  tag: { color: '#e06c75' },
  boolean: { color: '#d19a66' },
  number: { color: '#d19a66' },
  constant: { color: '#d19a66' },
  selector: { color: '#98c379' },
  'attr-name': { color: '#d19a66' },
  string: { color: '#98c379' },
  char: { color: '#98c379' },
  builtin: { color: '#56b6c2' },
  operator: { color: '#56b6c2' },
  url: { color: '#56b6c2' },
  variable: { color: '#e06c75' },
  atrule: { color: '#c678dd' },
  'attr-value': { color: '#98c379' },
  function: { color: '#61afef' },
  'class-name': { color: '#e5c07b' },
  keyword: { color: '#c678dd' },
  regex: { color: '#98c379' },
};

function resolveLanguage(raw) {
  const key = String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/^language-/, '');
  if (!key) return 'text';
  const mapped = LANG_ALIASES[key] || key;
  if (mapped === 'text') return 'text';
  if (REGISTERED.has(mapped)) return mapped;
  return REGISTERED.has(key) ? key : 'text';
}

function looksLikeFilename(s) {
  const t = String(s || '').trim();
  if (!t || t.length > 120) return false;
  if (/^file:/i.test(t)) return true;
  return /^[\w.\- ()\u00C0-\u024F]+\.[a-z0-9]{1,12}$/i.test(t);
}

function mimeForFilename(name) {
  const n = String(name || '').toLowerCase();
  if (n.endsWith('.csv')) return 'text/csv;charset=utf-8';
  if (n.endsWith('.tsv')) return 'text/tab-separated-values;charset=utf-8';
  if (n.endsWith('.json')) return 'application/json;charset=utf-8';
  if (n.endsWith('.md')) return 'text/markdown;charset=utf-8';
  if (n.endsWith('.html') || n.endsWith('.htm')) return 'text/html;charset=utf-8';
  return 'text/plain;charset=utf-8';
}

function downloadTextFile(filename, content, mimeType) {
  const blob = new Blob([content], { type: mimeType || mimeForFilename(filename) });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'datei.txt';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function isImageFilename(name) {
  let t = String(name || '').trim();
  if (!t) return false;
  t = t.replace(/^file:/i, '').replace(/^["'`]+|["'`]+$/g, '').trim();
  t = t.split(/[/\\]/).pop() || t;
  t = t.split('?')[0].split('#')[0];
  return /\.(png|jpe?g|webp|gif|bmp|svg|ico|avif|heic|tiff?)$/i.test(t);
}

function isImageFenceLanguage(lang) {
  const t = String(lang || '')
    .trim()
    .toLowerCase()
    .replace(/^file:/i, '');
  return /^(png|jpe?g|jpg|webp|gif|bmp|svg|ico|avif|heic|tiff?|image|img)$/i.test(t) || isImageFilename(t);
}

function isFilePreviewLang(language, filename) {
  const lang = String(language || '').toLowerCase();
  const name = String(filename || '').toLowerCase();
  // Fake-Bilder als Text-„DATEI“ nicht anzeigen (Modell erfindet oft ```file:….jpg```)
  if (isImageFilename(name) || isImageFenceLanguage(lang) || isImageFilename(lang.replace(/^file:/i, ''))) {
    return false;
  }
  if (filename) return true;
  return ['csv', 'tsv', 'json', 'txt', 'md', 'markdown', 'xml', 'html', 'sql', 'yaml', 'yml', 'file', 'download'].includes(
    lang
  ) || /\.(csv|tsv|json|txt|md|xml|html?|sql|ya?ml)$/i.test(name);
}

/** Datei-Vorschau: Zeilennummern, markierbarer Text, Download oben */
function FilePreview({ language, code, open = false, filename = '' }) {
  const [copied, setCopied] = useState(false);
  const extFromLang = ['csv', 'tsv', 'json', 'txt', 'md', 'sql', 'html', 'xml', 'yaml', 'yml'].includes(
    String(language || '').toLowerCase()
  )
    ? String(language).toLowerCase() === 'markdown'
      ? 'md'
      : String(language).toLowerCase()
    : 'txt';
  const name = filename || `ki-export.${extFromLang}`;

  const raw = String(code || '');
  // Kein Trailing-Newline abschneiden und dann splitten — sonst driftet die Zählung;
  // leere letzte Zeile nach finalem \n weglassen (üblich bei CSV/Code).
  const lines = (() => {
    if (!raw.length) return [''];
    const parts = raw.split(/\r?\n/);
    if (parts.length > 1 && parts[parts.length - 1] === '') parts.pop();
    return parts.length ? parts : [''];
  })();
  const lineCount = lines.length;
  const isCsv = /\.csv$/i.test(name) || String(language || '').toLowerCase() === 'csv';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(raw);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    } catch (_) {
      /* ignore */
    }
  };

  const download = () => {
    downloadTextFile(name, raw, mimeForFilename(name));
  };

  return (
    <div className="ai-chat-file-preview my-2 overflow-hidden rounded-xl border border-emerald-500/30 bg-[#0c0f14] shadow-sm dark:border-emerald-500/25">
      <div className="flex items-center justify-between gap-2 border-b border-emerald-500/20 bg-[#0f1614] px-3 py-2">
        <div className="min-w-0 flex items-center gap-2">
          <span className="inline-flex items-center rounded-md bg-emerald-500/20 px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wider text-emerald-300">
            {isCsv ? 'CSV' : 'Datei'}
          </span>
          <span className="truncate text-[12px] font-medium text-neutral-100" title={name}>
            {name}
          </span>
          <span className="flex-shrink-0 text-[10px] tabular-nums text-neutral-500">
            {lineCount} Zeile{lineCount === 1 ? '' : 'n'}
          </span>
          {open ? (
            <span className="text-[10px] font-medium text-emerald-400/80">schreibt…</span>
          ) : null}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            type="button"
            onClick={download}
            className="inline-flex items-center gap-1 rounded-md bg-emerald-500/20 px-2 py-1 text-[11px] font-semibold text-emerald-200 transition-colors hover:bg-emerald-500/30"
            title="Datei herunterladen"
          >
            <Download className="h-3.5 w-3.5" />
            Download
          </button>
          <button
            type="button"
            onClick={copy}
            className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[10px] font-medium text-neutral-400 transition-colors hover:bg-neutral-800 hover:text-neutral-100"
            title="Inhalt kopieren"
          >
            {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
            {copied ? 'Kopiert' : 'Kopieren'}
          </button>
        </div>
      </div>
      <div className="ai-chat-file-preview__scroll max-h-[min(28rem,55vh)] overflow-auto">
        {/* Eine Zeile = Nummer + Text → Höhen können nicht auseinanderlaufen */}
        <div className="ai-chat-file-preview__rows">
          {lines.map((line, i) => (
            <div key={`row-${i}`} className="ai-chat-file-preview__row">
              <span className="ai-chat-file-preview__num" aria-hidden="true">
                {i + 1}
              </span>
              <span className="ai-chat-file-preview__line">{line || '\u00a0'}</span>
            </div>
          ))}
          {open ? (
            <div className="ai-chat-file-preview__row ai-chat-file-preview__row--cursor">
              <span className="ai-chat-file-preview__num" aria-hidden="true" />
              <span className="ai-chat-file-preview__line">
                <span className="ai-chat-cursor" aria-hidden="true" />
              </span>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function CodeBlock({ language, code, open = false, filename = '' }) {
  const [copied, setCopied] = useState(false);
  const prismLang = resolveLanguage(language);
  const displayLang =
    language && language !== 'text'
      ? language.startsWith('file:')
        ? 'file'
        : language
      : 'code';
  const label = filename || displayLang;
  const codeText = String(code || '');
  const canHighlight = prismLang !== 'text' && codeText.trim().length > 0;
  const firstBodyLine = codeText.split(/\r?\n/).find((l) => l.trim()) || '';
  const fakeImageName =
    (isImageFilename(filename) && String(filename).replace(/^file:/i, '').replace(/^["'`]+|["'`]+$/g, '')) ||
    (isImageFenceLanguage(language) &&
      (String(language || '').replace(/^file:/i, '') || filename || 'bild')) ||
    (isImageFilename(String(language || '').replace(/^file:/i, '')) &&
      String(language || '').replace(/^file:/i, '')) ||
    ((String(language || '').toLowerCase() === 'file' ||
      String(language || '').toLowerCase() === 'download' ||
      String(language || '').toLowerCase() === 'attachment' ||
      String(language || '').toLowerCase() === 'text') &&
      isImageFilename(firstBodyLine) &&
      firstBodyLine.replace(/^file:/i, '').replace(/^["'`]+|["'`]+$/g, '').trim());
  const canDownload = codeText.trim().length > 0 && !fakeImageName;
  // Eigene Nummerierung — eine DOM-Zeile = Nummer + Text (kein Höhen-Drift)
  const displayLines = (() => {
    if (!codeText) return [''];
    const parts = codeText.split(/\r?\n/);
    if (parts.length > 1 && parts[parts.length - 1] === '') parts.pop();
    return parts.length ? parts : [''];
  })();
  const showGutter = displayLines.length > 3;

  if (fakeImageName) {
    // Kein DATEI-Download und kein Prompt-Text als „Bild“ — Server liefert echtes Bild separat
    return null;
  }

  // CSV ohne Tag / verschachtelte Fence → immer FilePreview, nie generisches „CODE“
  let effectiveLang = language;
  let effectiveCode = codeText;
  let effectiveFile = filename;
  if (String(effectiveCode || '').trim().startsWith('```')) {
    const unwrapped = unwrapNestedFence(effectiveCode);
    if (unwrapped) {
      if (unwrapped.language) effectiveLang = unwrapped.language;
      effectiveCode = unwrapped.body;
    }
  }
  if (
    (!effectiveLang || effectiveLang === 'text' || effectiveLang === 'code') &&
    looksLikeCsvContent(effectiveCode)
  ) {
    effectiveLang = 'csv';
    if (!effectiveFile) effectiveFile = 'ki-export.csv';
  }

  if (isFilePreviewLang(effectiveLang, effectiveFile) || looksLikeCsvContent(effectiveCode)) {
    return (
      <FilePreview
        language={looksLikeCsvContent(effectiveCode) ? 'csv' : effectiveLang}
        code={effectiveCode}
        open={open}
        filename={effectiveFile || (looksLikeCsvContent(effectiveCode) ? 'ki-export.csv' : '')}
      />
    );
  }

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(codeText);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    } catch (_) {
      /* ignore */
    }
  };

  const download = () => {
    const name =
      filename ||
      `ki-${displayLang || 'text'}-${Date.now()}.${
        ['csv', 'tsv', 'json', 'txt', 'md', 'sql', 'html', 'xml', 'yaml', 'yml'].includes(
          String(language || '').toLowerCase()
        )
          ? String(language).toLowerCase()
          : 'txt'
      }`;
    downloadTextFile(name, codeText, mimeForFilename(name));
  };

  return (
    <div className="ai-chat-codeblock my-2 overflow-hidden rounded-xl border border-neutral-800/90 bg-[#0c0f14] shadow-sm dark:border-neutral-700">
      <div className="flex items-center justify-between gap-2 border-b border-neutral-800 bg-[#12161e] px-3 py-1.5">
        <span
          className="inline-flex items-center gap-1.5 rounded-md bg-emerald-500/15 px-1.5 py-0.5 font-mono text-[10px] font-semibold tracking-wider text-emerald-300 truncate max-w-[60%]"
          title={label}
        >
          <span className="uppercase">{filename ? 'Datei' : displayLang}</span>
          {filename ? <span className="normal-case font-medium truncate">{filename}</span> : null}
          {open ? (
            <span className="normal-case tracking-normal font-medium text-emerald-400/80">schreibt…</span>
          ) : null}
        </span>
        <div className="flex items-center gap-0.5 flex-shrink-0">
          {canDownload ? (
            <button
              type="button"
              onClick={download}
              className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-medium text-neutral-400 transition-colors hover:bg-neutral-800 hover:text-neutral-100"
              title="Datei herunterladen"
            >
              <Download className="h-3 w-3" />
              Download
            </button>
          ) : null}
          <button
            type="button"
            onClick={copy}
            className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-medium text-neutral-400 transition-colors hover:bg-neutral-800 hover:text-neutral-100"
            title="Code kopieren"
          >
            {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
            {copied ? 'Kopiert' : 'Kopieren'}
          </button>
        </div>
      </div>
      <div className="ai-chat-codeblock__scroll overflow-auto max-h-[min(28rem,55vh)]">
        {showGutter ? (
          <div className="ai-chat-codeblock__rows">
            {displayLines.map((line, i) => (
              <div key={`row-${i}`} className="ai-chat-codeblock__row">
                <span className="ai-chat-codeblock__num" aria-hidden="true">
                  {i + 1}
                </span>
                <span className="ai-chat-codeblock__line">{line || '\u00a0'}</span>
              </div>
            ))}
            {open ? (
              <div className="ai-chat-codeblock__row ai-chat-codeblock__row--cursor">
                <span className="ai-chat-codeblock__num" aria-hidden="true" />
                <span className="ai-chat-codeblock__line">
                  <span className="ai-chat-cursor" aria-hidden="true" />
                </span>
              </div>
            ) : null}
          </div>
        ) : (
          <div className="ai-chat-codeblock__code min-w-0">
            {canHighlight ? (
              <SyntaxHighlighter
                language={prismLang}
                style={CODE_STYLE}
                useInlineStyles
                customStyle={{
                  margin: 0,
                  padding: '0.85rem 1rem 0.85rem 0.75rem',
                  background: 'transparent',
                  fontSize: '12px',
                  lineHeight: 1.55,
                  color: '#abb2bf',
                  fontFamily:
                    'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                  userSelect: 'text',
                  whiteSpace: 'pre',
                }}
                codeTagProps={{
                  className: `language-${prismLang}`,
                  style: {
                    fontFamily: 'inherit',
                    fontSize: 'inherit',
                    lineHeight: 'inherit',
                    background: 'transparent',
                    color: '#abb2bf',
                    textShadow: 'none',
                    userSelect: 'text',
                    whiteSpace: 'pre',
                  },
                }}
                PreTag="div"
                className={`ai-chat-codeblock__pre language-${prismLang}`}
                showLineNumbers={false}
                wrapLongLines={false}
              >
                {displayLines.join('\n')}
              </SyntaxHighlighter>
            ) : (
              <pre className="ai-chat-codeblock__pre px-3.5 py-3 text-[12px] leading-[1.55] select-text whitespace-pre">
                <code className="font-mono select-text" style={{ color: '#abb2bf' }}>
                  {displayLines.map((line, i) => (
                    <React.Fragment key={`raw-${i}`}>
                      {i > 0 ? '\n' : null}
                      {line}
                    </React.Fragment>
                  ))}
                  {open ? <span className="ai-chat-cursor" aria-hidden="true" /> : null}
                </code>
              </pre>
            )}
            {canHighlight && open ? (
              <div className="px-3.5 pb-2 -mt-1">
                <span className="ai-chat-cursor" aria-hidden="true" />
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

/** Inline: `code`, **fett**, *kursiv*, __fett__, _kursiv_, [text](url) */
function renderInline(text, keyPrefix = 'i') {
  const src = String(text || '');
  const parts = [];
  // Reihenfolge: code → html-a → md-links → bold → italic
  const re =
    /(`[^`\n]+`)|(<a\s+[^>]*href\s*=\s*["'][^"']+["'][^>]*>[\s\S]*?<\/a>)|(\[[^\]]+\]\([^)\s]+\))|(\*\*[^*]+?\*\*)|(__[^_]+?__)|(\*[^*\n]+?\*)|(_[^_\n]+?_)/gi;
  let last = 0;
  let m;
  let k = 0;
  while ((m = re.exec(src)) !== null) {
    if (m.index > last) {
      parts.push(<React.Fragment key={`${keyPrefix}-t${k++}`}>{src.slice(last, m.index)}</React.Fragment>);
    }
    const token = m[0];
    if (m[1]) {
      parts.push(
        <code key={`${keyPrefix}-c${k++}`} className="ai-chat-inline-code rounded-md px-1.5 py-0.5 text-[12px] font-mono">
          {token.slice(1, -1)}
        </code>
      );
    } else if (m[2]) {
      const hm = /href\s*=\s*["']([^"']+)["'][^>]*>([\s\S]*?)<\/a>/i.exec(token);
      const href = hm?.[1] || '#';
      const label = String(hm?.[2] || href)
        .replace(/<[^>]+>/g, '')
        .replace(/\s+/g, ' ')
        .trim();
      parts.push(
        <a
          key={`${keyPrefix}-ha${k++}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="ai-md-a"
        >
          {label || href}
        </a>
      );
    } else if (m[3]) {
      const lm = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(token);
      parts.push(
        <a
          key={`${keyPrefix}-a${k++}`}
          href={lm[2]}
          target="_blank"
          rel="noopener noreferrer"
          className="ai-md-a"
        >
          {lm[1]}
        </a>
      );
    } else if (m[4] || m[5]) {
      const inner = token.slice(2, -2);
      parts.push(
        <strong key={`${keyPrefix}-b${k++}`} className="ai-md-strong" style={{ fontWeight: 700 }}>
          {renderInline(inner, `${keyPrefix}-b${k}`)}
        </strong>
      );
    } else if (m[6] || m[7]) {
      const inner = token.slice(1, -1);
      parts.push(
        <em key={`${keyPrefix}-e${k++}`} className="ai-md-em" style={{ fontStyle: 'italic' }}>
          {renderInline(inner, `${keyPrefix}-e${k}`)}
        </em>
      );
    }
    last = m.index + token.length;
  }
  if (last < src.length) {
    parts.push(<React.Fragment key={`${keyPrefix}-t${k++}`}>{src.slice(last)}</React.Fragment>);
  }
  return parts.length ? parts : src;
}

/** Entfernt irrtümliche Meta-Zeilen wie „Code“ / „Codeblock“ aus Fence-Inhalten */
function normalizeFencedCode(body) {
  return String(body || '')
    .replace(/^[ \t]*(?:code(?:block|blöcke?)?|source|snippet)[ \t]*\r?\n/i, '')
    .replace(/^\n/, '')
    .replace(/\n$/, '');
}

/** CSV/TSV-Erkennung auch ohne Sprach-Tag (Semikolon/Tab/Komma) */
function looksLikeCsvContent(text) {
  const lines = String(text || '')
    .replace(/^\uFEFF/, '')
    .trim()
    .split(/\n/)
    .map((l) => l.trimEnd())
    .filter((l) => l.trim().length);
  if (lines.length < 1) return false;
  const header = lines[0];
  if (/^```/.test(header)) return false;
  let sep = null;
  if (header.includes('\t')) sep = '\t';
  else if ((header.match(/;/g) || []).length >= 2) sep = ';';
  else if ((header.match(/,/g) || []).length >= 2) sep = ',';
  if (!sep) return false;
  const cols = header.split(sep).map((c) => c.trim()).filter(Boolean);
  if (cols.length < 2) return false;
  if (/firma|unternehmen|name|email|e-mail|adresse|straße|strasse|plz|ort|telefon|phone|webseite|website|url|domain/i.test(header)) {
    return true;
  }
  if (lines.length < 2) return cols.length >= 3;
  const similar = lines.slice(1, 8).filter((l) => {
    const n = l.split(sep).length;
    return Math.abs(n - cols.length) <= 1 && n >= 2;
  });
  return similar.length >= Math.min(1, lines.length - 1);
}

/**
 * Modell packt oft ``` um ```csv … ``` — innere Fence entpacken.
 */
function unwrapNestedFence(body) {
  let b = String(body || '').trim();
  if (!b.startsWith('```')) return null;

  // Komplett verschachtelt: ```lang\n…\n```
  const full = /^```([a-zA-Z0-9_+#.:-]*)[^\n]*\r?\n([\s\S]*?)\r?\n```\s*$/i.exec(b);
  if (full) {
    return {
      language: String(full[1] || '').trim().toLowerCase() || null,
      body: normalizeFencedCode(full[2]),
    };
  }

  // Nur öffnende innere Fence-Zeile (Streaming / kaputt)
  const lines = b.split(/\n/);
  if (lines[0] && /^```/.test(lines[0])) {
    const lang = lines[0].replace(/^```/, '').trim().split(/\s+/)[0].toLowerCase();
    let rest = lines.slice(1).join('\n');
    rest = rest.replace(/\n```\s*$/m, '');
    return {
      language: lang || null,
      body: normalizeFencedCode(rest),
    };
  }
  return null;
}

function isCsvishSegment(seg) {
  if (!seg || seg.type !== 'code') return false;
  const lang = String(seg.language || '').toLowerCase();
  if (lang === 'csv' || lang === 'tsv') return true;
  if (/\.csv$/i.test(seg.filename || '')) return true;
  return looksLikeCsvContent(seg.value);
}

function refineCodeSegment(seg) {
  let language = String(seg.language || 'text').toLowerCase() || 'text';
  let value = String(seg.value || '');
  let filename = seg.filename || '';

  let guard = 0;
  while (guard < 3 && value.trim().startsWith('```')) {
    const unwrapped = unwrapNestedFence(value);
    if (!unwrapped) break;
    if (unwrapped.language) language = unwrapped.language;
    value = unwrapped.body;
    guard += 1;
  }

  if ((language === 'text' || language === 'code' || !language) && looksLikeCsvContent(value)) {
    language = 'csv';
  }
  if ((language === 'text' || language === 'code') && looksLikeCsvContent(value.replace(/^\uFEFF/, ''))) {
    language = 'csv';
  }
  if (!filename && language === 'csv') filename = 'ki-export.csv';
  if (!filename && language === 'tsv') filename = 'ki-export.tsv';

  return {
    ...seg,
    language,
    filename,
    value: normalizeFencedCode(value),
  };
}

/** Doppelte CSV-/Code-Blöcke (klein + groß) zu einem FilePreview zusammenziehen */
function consolidateSegments(segments) {
  const out = [];
  for (const raw of segments) {
    if (raw.type !== 'code') {
      out.push(raw);
      continue;
    }
    const seg = refineCodeSegment(raw);
    if (!String(seg.value || '').trim()) continue;

    const prev = out[out.length - 1];
    if (prev && isCsvishSegment(prev) && isCsvishSegment(seg)) {
      const prevLines = String(prev.value).split('\n').length;
      const nextLines = String(seg.value).split('\n').length;
      // Gleiche Header-Zeile → größeren Block behalten
      const prevHead = String(prev.value).split('\n').find((l) => l.trim()) || '';
      const nextHead = String(seg.value).split('\n').find((l) => l.trim()) || '';
      if (prevHead && nextHead && prevHead === nextHead) {
        if (nextLines >= prevLines) {
          out[out.length - 1] = {
            ...seg,
            language: 'csv',
            filename: seg.filename || prev.filename || 'ki-export.csv',
          };
        }
        continue;
      }
    }
    out.push(seg);
  }
  return out;
}

function parseBlocks(raw, { allowOpenFence = false } = {}) {
  const text = String(raw || '').replace(/\r\n/g, '\n');
  const segments = [];
  // Sprache optional; Dateiname in derselben Zeile bleibt Meta (nicht Body)
  const fenceRe = /```([a-zA-Z0-9_+#.:-]*)[ \t]*([^\n]*)\n?([\s\S]*?)```/g;
  let last = 0;
  let m;
  while ((m = fenceRe.exec(text)) !== null) {
    if (m.index > last) {
      segments.push({ type: 'md', value: text.slice(last, m.index) });
    }
    const langRaw = String(m[1] || '').trim();
    const sameLine = String(m[2] || '').trim();
    const rest = String(m[3] || '');
    let language = langRaw.toLowerCase() || 'text';
    let filename = '';
    let body = rest;

    if (language.startsWith('file:')) {
      filename = language.slice(5);
      language = 'text';
    } else if (isImageFenceLanguage(language) || isImageFilename(language)) {
      filename = language.replace(/^file:/i, '') || language;
      language = 'text';
    } else if (
      (language === 'file' || language === 'download' || language === 'attachment') &&
      looksLikeFilename(sameLine)
    ) {
      filename = sameLine.replace(/^file:/i, '');
      language = 'text';
    } else if (looksLikeFilename(sameLine)) {
      filename = sameLine.replace(/^file:/i, '');
    } else if (
      (language === 'file' || language === 'download' || language === 'attachment') &&
      !sameLine
    ) {
      const firstLine = rest.split('\n').find((l) => l.trim()) || '';
      if (isImageFilename(firstLine) || looksLikeFilename(firstLine)) {
        filename = firstLine.trim().replace(/^file:/i, '');
        body = rest.split('\n').slice(1).join('\n');
      }
    } else if (sameLine) {
      body = `${sameLine}\n${rest}`;
    }

    segments.push({
      type: 'code',
      language,
      filename,
      value: normalizeFencedCode(body),
    });
    last = m.index + m[0].length;
  }
  if (last < text.length) {
    const rem = text.slice(last);
    if (allowOpenFence) {
      const openIdx = rem.indexOf('```');
      if (openIdx >= 0) {
        const before = rem.slice(0, openIdx);
        const fencePart = rem.slice(openIdx);
        const om = /^```([a-zA-Z0-9_+#.:-]*)[ \t]*([^\n]*)(?:\n|$)([\s\S]*)$/.exec(fencePart);
        if (om && !/```/.test(String(om[3] || ''))) {
          if (before) segments.push({ type: 'md', value: before });
          const langRaw = String(om[1] || '').trim();
          const sameLine = String(om[2] || '').trim();
          const rest = String(om[3] || '');
          let language = langRaw.toLowerCase() || 'text';
          let filename = '';
          let body = rest;
          if (language.startsWith('file:')) {
            filename = language.slice(5);
            language = 'text';
          } else if (isImageFenceLanguage(language) || isImageFilename(language)) {
            filename = language.replace(/^file:/i, '') || language;
            language = 'text';
          } else if (looksLikeFilename(sameLine)) {
            filename = sameLine.replace(/^file:/i, '');
          } else if (
            (language === 'file' || language === 'download' || language === 'attachment') &&
            !sameLine
          ) {
            const firstLine = rest.split('\n').find((l) => l.trim()) || '';
            if (isImageFilename(firstLine) || looksLikeFilename(firstLine)) {
              filename = firstLine.trim().replace(/^file:/i, '');
              body = rest.split('\n').slice(1).join('\n');
            }
          } else if (sameLine) {
            body = `${sameLine}\n${rest}`;
          }
          segments.push({
            type: 'code',
            language,
            filename,
            value: normalizeFencedCode(body),
            open: true,
          });
          return consolidateSegments(segments.length ? segments : [{ type: 'md', value: text }]);
        }
      }
    }
    segments.push({ type: 'md', value: rem });
  }
  if (!segments.length) {
    segments.push({ type: 'md', value: text });
  }
  return consolidateSegments(segments);
}

function isUl(line) {
  return /^\s*[-*+]\s+/.test(line);
}
function isOl(line) {
  return /^\s*\d+[.)]\s+/.test(line);
}
function isQuote(line) {
  return /^\s*>\s?/.test(line);
}
function isHeading(line) {
  return /^(#{1,6})\s+\S/.test(line);
}
function isHr(line) {
  return /^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(line);
}

function splitTableCells(line) {
  let s = String(line || '').trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|')) s = s.slice(0, -1);
  return s.split('|').map((c) => c.trim());
}

/** GFM-Trenner: | :--- | :---: | ---: | */
function isTableSep(line) {
  const s = String(line || '').trim();
  if (!s || !s.includes('-')) return false;
  if (!/^[\s|:-]+$/.test(s)) return false;
  const cells = splitTableCells(s).filter((c) => c.length > 0);
  if (cells.length < 1) return false;
  return cells.every((c) => /^:?-{3,}:?$/.test(c));
}

function tableAlign(cell) {
  const c = String(cell || '').trim();
  const left = c.startsWith(':');
  const right = c.endsWith(':');
  if (left && right) return 'center';
  if (right) return 'right';
  if (left) return 'left';
  return undefined;
}

function tryParseTable(lines, start) {
  if (start + 1 >= lines.length) return null;
  const headerLine = lines[start];
  const sepLine = lines[start + 1];
  if (!String(headerLine || '').includes('|') || !isTableSep(sepLine)) return null;
  if (
    isHeading(headerLine) ||
    isUl(headerLine) ||
    isOl(headerLine) ||
    isQuote(headerLine) ||
    isHr(headerLine)
  ) {
    return null;
  }

  const headers = splitTableCells(headerLine);
  if (!headers.length) return null;
  const aligns = splitTableCells(sepLine).map(tableAlign);
  const rows = [];
  let i = start + 2;
  while (i < lines.length) {
    const row = lines[i];
    if (!String(row || '').trim()) break;
    if (!String(row).includes('|') || isTableSep(row)) break;
    if (isHeading(row) || isHr(row) || isUl(row) || isOl(row) || isQuote(row)) break;
    rows.push(splitTableCells(row));
    i += 1;
  }
  return { headers, aligns, rows, end: i };
}

function listItemText(line, ordered) {
  return ordered ? line.replace(/^\s*\d+[.)]\s+/, '') : line.replace(/^\s*[-*+]\s+/, '');
}

function renderMdBlocks(mdText, keyPrefix) {
  const lines = String(mdText || '').split('\n');
  const nodes = [];
  let i = 0;
  let k = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (!String(line).trim()) {
      i += 1;
      continue;
    }

    if (isHr(line)) {
      nodes.push(<hr key={`${keyPrefix}-hr${k++}`} className="ai-md-hr" />);
      i += 1;
      continue;
    }

    if (isHeading(line)) {
      const hm = /^(#{1,6})\s+(.*)$/.exec(line);
      const level = Math.min(6, hm[1].length);
      const Tag = `h${level}`;
      nodes.push(
        <Tag key={`${keyPrefix}-h${k++}`} className={`ai-md-h ai-md-h${level}`}>
          {renderInline(hm[2], `${keyPrefix}-h${k}`)}
        </Tag>
      );
      i += 1;
      continue;
    }

    const table = tryParseTable(lines, i);
    if (table) {
      const colCount = Math.max(
        table.headers.length,
        ...table.rows.map((r) => r.length),
        table.aligns.length
      );
      const pad = (cells) => {
        const out = cells.slice(0, colCount);
        while (out.length < colCount) out.push('');
        return out;
      };
      const alignAt = (idx) => table.aligns[idx];
      nodes.push(
        <div key={`${keyPrefix}-tbl${k++}`} className="ai-md-table-wrap">
          <table className="ai-md-table">
            <thead>
              <tr>
                {pad(table.headers).map((cell, idx) => (
                  <th
                    key={`${keyPrefix}-th${k}-${idx}`}
                    className="ai-md-th"
                    style={alignAt(idx) ? { textAlign: alignAt(idx) } : undefined}
                  >
                    {renderInline(cell, `${keyPrefix}-th${k}-${idx}`)}
                  </th>
                ))}
              </tr>
            </thead>
            {table.rows.length > 0 ? (
              <tbody>
                {table.rows.map((row, rIdx) => (
                  <tr key={`${keyPrefix}-tr${k}-${rIdx}`}>
                    {pad(row).map((cell, cIdx) => (
                      <td
                        key={`${keyPrefix}-td${k}-${rIdx}-${cIdx}`}
                        className="ai-md-td"
                        style={alignAt(cIdx) ? { textAlign: alignAt(cIdx) } : undefined}
                      >
                        {renderInline(cell, `${keyPrefix}-td${k}-${rIdx}-${cIdx}`)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            ) : null}
          </table>
        </div>
      );
      i = table.end;
      continue;
    }

    if (isUl(line) || isOl(line)) {
      const ordered = isOl(line);
      const items = [];
      // Leere Zeilen zwischen Punkten nicht als Listenende werten
      // (sonst startet jedes <ol> wieder bei 1.)
      while (i < lines.length) {
        const cur = lines[i];
        if (!String(cur).trim()) {
          let j = i + 1;
          while (j < lines.length && !String(lines[j]).trim()) j += 1;
          if (j < lines.length && (ordered ? isOl(lines[j]) : isUl(lines[j]))) {
            i = j;
            continue;
          }
          break;
        }
        if (!(ordered ? isOl(cur) : isUl(cur))) break;
        items.push(listItemText(cur, ordered));
        i += 1;
      }
      const ListTag = ordered ? 'ol' : 'ul';
      nodes.push(
        <ListTag key={`${keyPrefix}-l${k++}`} className={ordered ? 'ai-md-ol' : 'ai-md-ul'}>
          {items.map((item, idx) => (
            <li key={`${keyPrefix}-li${k}-${idx}`} className="ai-md-li">
              {renderInline(item, `${keyPrefix}-li${k}-${idx}`)}
            </li>
          ))}
        </ListTag>
      );
      continue;
    }

    if (isQuote(line)) {
      const quotes = [];
      while (i < lines.length && isQuote(lines[i])) {
        quotes.push(lines[i].replace(/^\s*>\s?/, ''));
        i += 1;
      }
      nodes.push(
        <blockquote key={`${keyPrefix}-q${k++}`} className="ai-md-blockquote">
          {renderInline(quotes.join(' '), `${keyPrefix}-q${k}`)}
        </blockquote>
      );
      continue;
    }

    // Absatz: zusammenhängende Nicht-Sonderzeilen
    const para = [];
    while (
      i < lines.length &&
      String(lines[i]).trim() &&
      !isUl(lines[i]) &&
      !isOl(lines[i]) &&
      !isHeading(lines[i]) &&
      !isQuote(lines[i]) &&
      !isHr(lines[i]) &&
      !tryParseTable(lines, i)
    ) {
      para.push(lines[i]);
      i += 1;
    }
    nodes.push(
      <p key={`${keyPrefix}-p${k++}`} className="ai-md-p">
        {para.map((pl, idx) => (
          <React.Fragment key={`${keyPrefix}-pl${k}-${idx}`}>
            {idx > 0 ? <br /> : null}
            {renderInline(pl, `${keyPrefix}-pl${k}-${idx}`)}
          </React.Fragment>
        ))}
      </p>
    );
  }

  return nodes;
}

/**
 * Rendert Chat-Inhalt als Markdown (fett, kursiv, Listen, Tabellen, Überschriften, Code …).
 * Eigener Parser — unabhängig von ESM-Markdown-Libs.
 */
export default function AiChatMarkdown({ content, tone = 'assistant', streaming = false }) {
  const segments = useMemo(() => {
    // Client-Fallback: Fake-Bild-Fences ausblenden (Server sollte sie schon entfernen)
    const cleaned = String(content || '')
      .replace(/```[^\n]*\.(?:png|jpe?g|webp|gif|bmp|svg|ico|avif|heic|tiff?)[^\n]*\n[\s\S]*?```/gi, '\n')
      .replace(/```(?:file:)?(?:png|jpe?g|jpg|webp|gif|bmp|svg|image|img)\b[^\n]*\n[\s\S]*?```/gi, '\n')
      .replace(
        /```(?:file|download|attachment)\b[^\n]*\n(?:[^\n]*\.(?:png|jpe?g|webp|gif|bmp|svg)[^\n]*\n)?[\s\S]*?```/gi,
        '\n'
      );
    // Auch nach Abbruch offene Codeblöcke als Code (mit Download) rendern
    return parseBlocks(cleaned, { allowOpenFence: true });
  }, [content]);

  return (
    <div
      className={`ai-chat-md text-[13px] leading-relaxed break-words ${
        tone === 'user' ? 'ai-chat-md--user' : ''
      }${streaming ? ' ai-chat-md--streaming' : ''}`}
    >
      {segments.map((seg, i) => {
        if (seg.type === 'code') {
          return (
            <CodeBlock
              key={`code-${i}`}
              language={seg.language}
              code={seg.value}
              filename={seg.filename || ''}
              open={Boolean(streaming && seg.open)}
            />
          );
        }
        return <React.Fragment key={`md-${i}`}>{renderMdBlocks(seg.value, `s${i}`)}</React.Fragment>;
      })}
      {streaming && !segments.some((s) => s.open) ? (
        <span className="ai-chat-cursor" aria-hidden="true" />
      ) : null}
    </div>
  );
}
